import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Code, CopyButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

const EVENTS = [
  'order.placed',
  'order.paid',
  'order.fulfilled',
  'order.cancelled',
  'order.refunded',
];

/**
 * Create/edit a webhook subscription. On create, after a successful save
 * the dialog switches to "here is your secret — copy it now" mode; the
 * secret is never re-shown.
 */
export default function HookDialog({ open, onClose, editing, onSaved, brandId }) {
  const [form, setForm] = useState({ name: '', url: '', events: [], status: 'active' });
  const [saving, setSaving] = useState(false);
  const [createdSecret, setCreatedSecret] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name || '',
        url: editing.url || '',
        events: editing.events || [],
        status: editing.status || 'active',
      });
    } else {
      setForm({ name: '', url: '', events: [], status: 'active' });
    }
    setCreatedSecret('');
  }, [open, editing]);

  const toggleEvent = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev],
    }));
  };

  const save = async () => {
    if (!form.url) { toast.error('URL is required'); return; }
    setSaving(true);
    try {
      let res;
      if (editing) {
        res = await API.updateBrandWebhook({
          brand_id: brandId, _id: editing._id,
          patch: { name: form.name, url: form.url, events: form.events, status: form.status },
        });
      } else {
        res = await API.createBrandWebhook({
          brand_id: brandId,
          name: form.name, url: form.url, events: form.events,
        });
      }
      if (!editing && res?.secret) {
        setCreatedSecret(res.secret);
        toast.success('Webhook created — copy the secret now');
      } else {
        toast.success('Saved');
        onSaved && onSaved();
        onClose();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => { onSaved && onSaved(); onClose(); };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>{editing ? 'Edit webhook' : 'New webhook'}</DialogTitle>
      <DialogContent dividers>
        {createdSecret ? (
          <Box>
            <Typography variant='body2' mb='6px'>
              Save this secret — we won't show it again. Use it to verify the{' '}
              <Code>X-TPO-Signature</Code> header on each delivery.
            </Typography>
            <Code sx={{ display: 'block', p: '8px', borderRadius: '8px' }}>
              {createdSecret}
            </Code>
            <CopyButton
              value={createdSecret}
              label='Copy secret'
              successMessage='Secret copied'
              sx={{ mt: '12px' }}
            />
          </Box>
        ) : (
          <Stack spacing='12px' sx={{ pt: '4px' }}>
            <TextField
              fullWidth size='small'
              label='Name'
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder='Order pipeline → CRM'
            />
            <TextField
              fullWidth size='small' required
              label='URL'
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder='https://hooks.example.com/tpo'
            />
            <Box>
              <Typography variant='body2' fontWeight='600' mb='4px'>Events</Typography>
              <Typography variant='caption' color='text.secondary' display='block' mb='6px'>
                Leave all unchecked to receive every event.
              </Typography>
              <Stack spacing='4px'>
                {EVENTS.map((ev) => (
                  <FormControlLabel
                    key={ev}
                    control={
                      <Switch
                        size='small'
                        checked={form.events.includes(ev)}
                        onChange={() => toggleEvent(ev)}
                      />
                    }
                    label={<Typography variant='body2'>{ev}</Typography>}
                  />
                ))}
              </Stack>
            </Box>
            {editing && (
              <FormControl size='small'>
                <InputLabel>Status</InputLabel>
                <Select
                  label='Status'
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <MenuItem value='active'>Active</MenuItem>
                  <MenuItem value='paused'>Paused</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {createdSecret ? (
          <Button variant='contained' onClick={dismiss}>I've saved it</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant='contained' onClick={save} disabled={saving}>
              {saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
